import { useState, useEffect, useRef } from 'react';

/**
 * Hook for managing inline name editing
 */
export function useNameEditing({ taskId, initialName, onRename }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(initialName);
  const inputRef = useRef(null);
  const nameSpanRef = useRef(null);
  const clickCursorPosRef = useRef(null);

  // Focus input when editing starts and set cursor position from click
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Set cursor position from click, or end if not available
      const pos = clickCursorPosRef.current ?? editValue.length;
      inputRef.current.setSelectionRange(pos, pos);
      clickCursorPosRef.current = null; // Reset for next time
    }
  }, [isEditing, editValue.length]);

  // Sync with prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(initialName);
    }
  }, [initialName, isEditing]);

  const startEditing = (e) => {
    e.stopPropagation();
    
    // Calculate cursor position from click location
    if (nameSpanRef.current && window.getSelection) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (range && nameSpanRef.current.contains(range.startContainer)) {
        clickCursorPosRef.current = range.startOffset;
      } else {
        clickCursorPosRef.current = initialName.length; // Default to end
      }
    } else {
      clickCursorPosRef.current = initialName.length;
    }
    
    setIsEditing(true);
    setEditValue(initialName);
  };

  const saveEdit = () => {
    if (editValue.trim() && editValue.trim() !== initialName) {
      onRename?.(taskId, editValue.trim());
    }
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setEditValue(initialName);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  return {
    inputRef,
    nameSpanRef,
    isEditing,
    editValue,
    setEditValue,
    startEditing,
    saveEdit,
    handleKeyDown,
  };
}

export default useNameEditing;

